import React, { useEffect, useState } from "react";
import SelectLabels from "../../components/SelectLabels";
import SalesChartSummary from "../../components/SalesChartSummary";
import { useMutation } from "@tanstack/react-query";
import { createData } from "../../utility/adminApi";
import { useNavigate } from "react-router-dom";

// Simple reusable Modal
function AiModal({ open, onClose }) {
  const navigate = useNavigate();

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-40"
      style={{ backdropFilter: "blur(2px)" }}
    >
      <div className="bg-white p-6 rounded-lg max-w-lg shadow-lg w-[90vw] md:w-auto">
        <h2 className="text-xl mb-2 font-bold text-blue-700">
          AI Sales Forecast
        </h2>
        <div className="text-gray-700 mb-4">
          <p>
            You need to activate AI Sales Forecasting to access projected sales for future periods like&nbsp;
            <span className="font-semibold text-blue-700">Next week</span> or&nbsp;
            <span className="font-semibold text-blue-700">Next month</span>.
          </p>
          <p className="mt-3">
            Please contact your administrator or check your settings to enable AI sales forecast features.
          </p>
        </div>
        <div className="flex flex-row gap-3 mt-2">
          <button
            className="px-5 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
            onClick={onClose}
          >
            Close
          </button>
          <button
            className="px-5 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition"
            onClick={() => navigate("/admin/profile")}
          >
            Activate AI
          </button>
        </div>
      </div>
    </div>
  );
}

function SalesSummary() {
  const [timeRange, setTimeRange] = useState(0);
  const [showAiModal, setShowAiModal] = useState(false);

  // Updated periods per instructions
  const period = [
    { id: 0, key: "7days", time: "This week" },
    { id: 1, key: "30days", time: "This month" },
    { id: 2, key: "12months", time: "This year" },
    { id: 3, key: "next7to14days", time: <span style={{ color: "#1976d2" }}>Next week (AI)</span> },
    { id: 4, key: "nextmonth", time: <span style={{ color: "#1976d2" }}>Next month (AI)</span> },
  ];

  const [newPeriod, setNewPeriod] = useState(period[0]?.key || "7days");
  const [dataset, setDataset] = useState();

  // Track the last valid (non-AI) period index, for fallback when closing modal
  const [lastValidPeriodIdx, setLastValidPeriodIdx] = useState(0);

  // Handle period selector changes
  const handleChange = (value) => {
    // If selecting an AI period (id 3 or 4), show modal and don't update chart
    if (value === 3 || value === 4) {
      setShowAiModal(true);
      // Don't update timeRange or newPeriod, keep the last valid selection highlighted
      return;
    }
    setTimeRange(value);
    setLastValidPeriodIdx(value); // Remember last real (non-AI) period
  };

  // When timeRange changes, set newPeriod for API fetch
  useEffect(() => {
    const selected = period[timeRange];
    if (selected) {
      setNewPeriod(selected.key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange]);

  const chartData = useMutation({
    mutationFn: (newPeriodParam) =>
      createData(
        `dashboard/sales-summary?date_filter=${newPeriodParam}`,
        null,
        true
      ),
  });

  // Fetch data when real period changes
  useEffect(() => {
    chartData.mutate(newPeriod);
    // eslint-disable-next-line
  }, [newPeriod]);

  // Set dataset state after fetching chart data
  useEffect(() => {
    setDataset(chartData?.data);
  }, [chartData]);

  const labels = dataset?.data?.labels || [];
  const datasets = dataset?.data?.datasets || [];

  const newChartData = {
    labels: labels,
    datasets: datasets.map((dataset, index) => ({
      label: dataset.label,
      data: dataset.data,
      backgroundColor: dataset.backgroundColor,
      borderColor: dataset.borderColor || "rgba(0, 0, 0, 0.1)",
      borderWidth: dataset.borderWidth || 1,
      yAxisID: `y-axis-${index}`, // Assign a unique y-axis ID to each dataset
    })),
  };

  // If AI modal closes, reset dropdown selection to last valid value
  const handleModalClose = () => {
    setShowAiModal(false);
    setTimeRange(lastValidPeriodIdx);
  };

  return (
    <>
      <div className="flex flex-col bg-white rounded-lg gap-6 p-4">
        <div className="flex flex-col md:flex-row justify-between  items-center ">
          <h3 className="text-2xl font-medium ">Sales Summary</h3>
          {/* Drop down below */}
          <div>
            <SelectLabels
              valueKey="id"
              labelKey="time"
              text="Select a period"
              categoryData={period}
              onSelectChange={handleChange}
              previousValue={timeRange}
            />
          </div>
        </div>
        {/* Chart will be added here */}
        <SalesChartSummary dataset={newChartData} datasets={datasets} />
      </div>
      <AiModal open={showAiModal} onClose={handleModalClose} />
    </>
  );
}

export default SalesSummary;
